<script setup lang="ts">
export type EmptyStateIcon = 'search' | 'fighters' | 'events' | 'error' | 'empty'
export type ActionIcon = 'reset' | 'none'

export interface ActiveFilter {
  label: string
  value: string
}

interface Props {
  title: string
  description?: string
  icon?: EmptyStateIcon
  actionLabel?: string
  actionIcon?: ActionIcon
  actionAriaLabel?: string
  activeFilters?: ActiveFilter[]
}

withDefaults(defineProps<Props>(), {
  description: '',
  icon: 'empty',
  actionLabel: '',
  actionIcon: 'none',
  actionAriaLabel: '',
  activeFilters: () => []
})

const emit = defineEmits<{
  action: []
}>()
</script>

<template>
  <div class="text-center py-12">
    <div class="mb-4">
      <!-- Search icon -->
      <svg
        v-if="icon === 'search'"
        class="w-16 h-16 mx-auto text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      </svg>

      <!-- Fighters icon -->
      <svg
        v-else-if="icon === 'fighters'"
        class="w-16 h-16 mx-auto text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>

      <!-- Events icon -->
      <svg
        v-else-if="icon === 'events'"
        class="w-16 h-16 mx-auto text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>

      <!-- Error icon -->
      <svg
        v-else-if="icon === 'error'"
        class="w-16 h-16 mx-auto text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>

      <!-- Empty/default icon -->
      <svg
        v-else
        class="w-16 h-16 mx-auto text-gray-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="1.5"
          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
        />
      </svg>
    </div>

    <p class="text-h4 text-gray-300 mb-2">{{ title }}</p>
    <p v-if="description && activeFilters.length === 0" class="text-gray-400 mb-4">{{ description }}</p>

    <!-- Active filters display -->
    <div v-if="activeFilters.length > 0" class="mb-4">
      <p class="text-gray-400 mb-3">Your current filters:</p>
      <div class="flex flex-wrap justify-center gap-2">
        <span
          v-for="filter in activeFilters"
          :key="filter.label"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-700 text-sm"
        >
          <span class="text-gray-400">{{ filter.label }}:</span>
          <span class="text-white font-medium">{{ filter.value }}</span>
        </span>
      </div>
    </div>

    <button
      v-if="actionLabel"
      class="btn btn-primary inline-flex items-center gap-1.5"
      :aria-label="actionAriaLabel || undefined"
      @click="emit('action')"
    >
      <svg
        v-if="actionIcon === 'reset'"
        class="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
        />
      </svg>
      {{ actionLabel }}
    </button>

    <slot />
  </div>
</template>
